/* StringType.js */

"use strict";

/**
 * @type {{Control:{Textfield:{InputText:InputTextControlComponent}}}}
 * @const
 */
const uiCore = require("@gluenetworks/steve_ui_core");

const InputTextControl = uiCore.Control.Textfield.InputText;

class StringType {
    /**
     * @typedef {{[pattern]: string}} StringInfo
     * @typedef {{String: StringInfo}} StringTypeInfo - String type info object
     */

    constructor() {
        /**
         * @type {?StringInfo}
         * @private
         */
        this._initialData = {};

        /**
         * Root html Element
         * @type {?HTMLDivElement}
         */
        this.rootElem = this._createRootElement();

        /**
         * @type {?InputTextControlComponent}
         */
        this.inputPattern = this._createInput({
            placeholder: "(?:)"
        });

        this._registerListeners();
    }

    /********************************************/
    /* START PRIVATE COMPONENT MODEL METHODS    */

    /********************************************/

    get rootElement() {
        return this.rootElem;
    }

    _registerListeners() {
        this.rootElem.addEventListener("click", (this._onRootClick = this._onRootClick.bind(this)));
    }

    _unregisterListeners() {
        this.rootElem.removeEventListener("click", this._onRootClick);
    }
    /********************************************/
    /* END PRIVATE COMPONENT MODEL METHODS      */
    /* START PUBLIC COMPONENT MODEL METHODS     */

    /********************************************/

    _shutdown() {
        this._unregisterListeners();

        return Promise.resolve()
            .then(() => this.inputPattern.shutdown())
            .then(() => {
                this.rootElem.remove();
            })
            .then(() => {
                this._initialData = null;
                this.inputPattern = null;
                this.rootElem = null;
            });
    }

    /********************************************/
    /* END COMPONENT MODEL METHODS              */
    /* START HANDLER METHODS                    */
    /********************************************/
    _onRootClick(e) {
        e.preventDefault();
        e.stopPropagation();
    }
    /********************************************/
    /* END HANDLER METHODS                      */
    /* START PRIVATE METHODS                    */
    /********************************************/

    /********************************************/
    /* END PRIVATE METHODS                      */
    /* START PRIVATE RENDER METHODS             */

    /********************************************/

    _render() {
        this.rootElem.innerHTML = "";
        this.rootElem.appendChild(this._createInputGroup("Regular Expression Pattern", this.inputPattern));
    }

    /**
     *
     * @returns {HTMLDivElement}
     * @private
     */
    _createRootElement() {
        const rootElem = document.createElement("div");
        rootElem.classList.add("string-editor", "inner-editor");
        rootElem.component = this;
        return rootElem;
    }

    /**
     *
     * @param {Object} options - input options
     * @returns {InputTextControlComponent}
     * @private
     */
    _createInput(options) {
        return new InputTextControl(Object.assign({}, options, { darkTheme: true }));
    }

    _createInputGroup(labelString, inputComponent) {
        const wrapper = document.createElement("div");
        const labelEl = document.createElement("label");
        const inputId = `input_${labelString.replace(/[^\w]/g, "-")}`;

        wrapper.classList.add("input-wrapper");

        labelEl.setAttribute("for", inputId);
        labelEl.innerText = labelString;

        inputComponent.setAttribute("id", inputId);
        inputComponent.setAttribute("name", labelString);

        wrapper.appendChild(labelEl);
        wrapper.appendChild(inputComponent.renderer.rootElement);
        return wrapper;
    }
    /********************************************/
    /* END PRIVATE RENDER METHODS               */
    /* START PRIVATE API METHODS                */

    /********************************************/

    /**
     *
     * @param {StringInfo} [data = {}]
     * @private
     */
    _resetData(data = {}) {
        this._initialData = data;
        this.inputPattern.setValue(data.hasOwnProperty("pattern") ? String(data.pattern) : "");
    }

    /**
     * @returns {StringInfo}
     * @private
     */
    _getData() {
        const data = {};
        const pattern = this.inputPattern.getValue();

        Object.assign(data, pattern && { pattern });

        return data;
    }

    _focus() {
        this.inputPattern.focus();
    }

    /********************************************/
    /*  END PRIVATE API METHODS                 */
    /* START PUBLIC API METHODS                 */

    /********************************************/

    destroy() {
        this._shutdown().catch(console.error);
    }

    /**
     *
     * @param {StringTypeInfo} stringTypeInfo - enum type info object
     */
    setData(stringTypeInfo) {
        const data = (stringTypeInfo && stringTypeInfo.String) || {};
        this._resetData(data);
        this._render();
        this._focus();
    }

    /**
     *
     * @returns {StringTypeInfo}
     */
    getData() {
        return {
            String: this._getData()
        };
    }

    /**
     *
     * @returns {boolean}
     */
    isValueChanged() {
        return JSON.stringify(this._initialData) !== JSON.stringify(this._getData());
    }

    /**
     *
     * @returns {{valid: boolean, msg: string}}
     */
    validate() {
        return {
            valid: true,
            msg: ""
        };
    }

    focus() {
        this._focus();
    }

    /********************************************/
    /* END PUBLIC API METHODS                   */
    /********************************************/
}

module.exports = StringType;
